import { Injectable, NotFoundException, ConflictException } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { Category, CategoryDocument } from '@/modules/category/schemas/category.schema';
import { CreateCategoryDto } from '@/modules/category/dto/create-category.dto';
import { UpdateCategoryDto } from '@/modules/category/dto/update-category.dto';
import { Product, ProductDocument } from '@/modules/product/schemas/product.schema';

@Injectable()
export class CategoryService {
  constructor(
    @InjectModel(Category.name) private categoryModel: Model<CategoryDocument>,
    @InjectModel(Product.name) private productModel: Model<ProductDocument>,
  ) {}

  async create(createCategoryDto: CreateCategoryDto) {
    // Check if slug already exists (check both en and ar)
    const existingCategoryEn = await this.categoryModel.findOne({
      'slug.en': createCategoryDto.slug.en,
    });
    const existingCategoryAr = await this.categoryModel.findOne({
      'slug.ar': createCategoryDto.slug.ar,
    });
    
    if (existingCategoryEn || existingCategoryAr) {
      throw new ConflictException('Category with this slug already exists');
    }

    const category = new this.categoryModel(createCategoryDto);
    return category.save();
  }

  async findAll(query: any) {
    const { page = 1, limit = 10 } = {page: +query.page, limit: +query.limit};
    const skip = (page - 1) * limit;
    const total = await this.categoryModel.countDocuments();
    const categories = await this.categoryModel
      .find()
      .populate('parentId', 'name slug')
      .sort({ sortOrder: 1, name: 1 })
      .skip(skip)
      .limit(limit)
      .exec();
    return {
      categories,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit),
      },
    };
  }

  // get list categories without pagination 
  async list(filter: string = 'all') {
    // Build query based on filter
    let query = {};
    
    if (filter === 'active') {
      query = { isActive: true };
    } else if (filter === 'inactive') {
      query = { isActive: false };
    }
    // if filter === 'all', query remains empty (returns all categories)
    
    return this.categoryModel
      .find(query)
      .populate('parentId', 'name slug')
      .sort({ sortOrder: 1, name: 1 })
      .exec();
  }

  async findOne(id: string) {
    const category = await this.categoryModel
      .findById(id)
      .populate('parentId', 'name slug')
      .exec();

    if (!category || !category.isActive) {
      throw new NotFoundException('Category not found');
    }

    return category;
  }

  async findBySlug(slug: string) {
    // Search by both English and Arabic slugs
    const category = await this.categoryModel
      .findOne({ 
        $or: [
          { 'slug.en': slug },
          { 'slug.ar': slug }
        ],
        isActive: true 
      })
      .populate('parentId', 'name slug')
      .exec();

    if (!category) {
      throw new NotFoundException('Category not found');
    }

    return category;
  }

  async update(id: string, updateCategoryDto: UpdateCategoryDto) {
    // Check if slug already exists (excluding current category)
    if (updateCategoryDto.slug) {
      const existingCategoryEn = await this.categoryModel.findOne({
        'slug.en': updateCategoryDto.slug.en,
        _id: { $ne: id },
      });
      const existingCategoryAr = await this.categoryModel.findOne({
        'slug.ar': updateCategoryDto.slug.ar,
        _id: { $ne: id },
      });
      
      if (existingCategoryEn || existingCategoryAr) {
        throw new ConflictException('Category with this slug already exists');
      }
    }

    const category = await this.categoryModel
      .findByIdAndUpdate(id, updateCategoryDto, { new: true })
      .populate('parentId', 'name slug')
      .exec();

    if (!category) {
      throw new NotFoundException('Category not found');
    }

    return category;
  }

  async remove(id: string) {
    // Check if category has children
    const hasChildren = await this.categoryModel.findOne({ parentId: id });
    if (hasChildren) {
      throw new ConflictException('Cannot delete category with subcategories');
    }
    // check if category has products
    const hasProducts = await this.productModel.findOne({ categoryId: id });
    if (hasProducts) {
      throw new ConflictException('Cannot delete category with products');
    }
    const category = await this.categoryModel.findByIdAndDelete(id);

    if (!category) {
      throw new NotFoundException('Category not found');
    }
    // delete all products in category
    return { message: 'Category successfully removed' };
  }

  async getHierarchy() {
    const categories = await this.categoryModel
      .find({ isActive: true })
      .sort({ sortOrder: 1, name: 1 })
      .exec();

    return this.buildHierarchy(categories);
  }

  async getChildren(parentId: string) {
    return this.categoryModel
      .find({ parentId, isActive: true })
      .sort({ sortOrder: 1, name: 1 })
      .exec();
  }

  async getRootCategories() {
    return this.categoryModel
      .find({ parentId: null, isActive: true })
      .sort({ sortOrder: 1, name: 1 })
      .exec();
  }

  private buildHierarchy(categories: CategoryDocument[], parentId: any = null): any[] {
    const result: any[] = [];
    
    for (const category of categories) {
      if (String(category.parentId) === String(parentId)) {
        const children = this.buildHierarchy(categories, category._id);
        const categoryObj = category.toObject();
        if (children.length > 0) {
          categoryObj.children = children;
        }
        result.push(categoryObj);
      }
    }
    
    return result;
  }

  async updateProductCount(categoryId: string, increment: number = 1) {
    await this.categoryModel.findByIdAndUpdate(
      categoryId,
      { $inc: { productCount: increment } },
    );
  }

  async updatePackageCount(categoryId: string, increment: number = 1) {
    await this.categoryModel.findByIdAndUpdate(
      categoryId,
      { $inc: { packageCount: increment } },
    );
  }
}

